import React from 'react';
import { Shield, Eye, FileCheck, RefreshCw } from 'lucide-react';

const securityData = {
  title: "AI-Native Security That Thinks Ahead",
  description: "Security intelligence built into every AI agent. Proactive threat detection. Automated compliance monitoring. Self-healing security protocols. Predictive vulnerability prevention.",
  keyPoints: [
    {
      title: "Built-in, not bolted-on security",
      description: "Security isn't an afterthought—it's woven into the DNA of every AI agent and system component from the ground up.",
      details: [
        "Zero-trust architecture by design",
        "Security-first development methodology",
        "Integrated threat modeling",
        "End-to-end encryption standards"
      ],
      icon: "Shield"
    },
    {
      title: "Proactive threat prevention",
      description: "AI-powered security that predicts and prevents attacks before they happen, not just responds after damage is done.",
      details: [
        "Behavioral anomaly detection",
        "Predictive threat intelligence",
        "Real-time risk assessment",
        "Automated incident prevention"
      ],
      icon: "Eye"
    },
    {
      title: "Domain-specific compliance (HIPAA, SOX, etc.)",
      description: "Industry-specific compliance frameworks built into each domain pack, ensuring regulatory requirements are met automatically.",
      details: [
        "HIPAA compliance for healthcare",
        "SOX compliance for financial services",
        "PCI-DSS for payment processing",
        "GDPR for data protection"
      ],
      icon: "FileCheck"
    },
    {
      title: "Self-healing protocols",
      description: "Autonomous security systems that detect, isolate, and repair security vulnerabilities without human intervention.",
      details: [
        "Automatic vulnerability patching",
        "Self-diagnostic security checks",
        "Autonomous threat isolation",
        "Continuous security optimization"
      ],
      icon: "RefreshCw"
    }
  ]
};

const iconMap = {
  Shield: Shield,
  Eye: Eye,
  FileCheck: FileCheck,
  RefreshCw: RefreshCw
};

const Security = () => {
  return (
    <section id="security" className="dark-full-container" style={{ 
      paddingTop: '160px', 
      paddingBottom: '160px',
      backgroundColor: 'var(--bg-secondary)',
      borderTop: '1px solid var(--border-subtle)',
      borderBottom: '1px solid var(--border-subtle)'
    }}>
      <div className="dark-content-container">
        <div className="reveal" style={{ textAlign: 'center', marginBottom: '100px' }}>
          <h2 className="display-large" style={{ marginBottom: '32px', color: 'var(--brand-primary)' }}>
            {securityData.title}
          </h2>
          <p className="body-large" style={{ 
            maxWidth: '900px', 
            margin: '0 auto',
            lineHeight: 1.6,
            fontSize: '20px'
          }}>
            {securityData.description}
          </p>
        </div>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '40px',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {securityData.keyPoints.map((point, index) => {
            const IconComponent = iconMap[point.icon];
            return (
              <div 
                key={index}
                className="ui-card reveal"
                style={{
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-subtle)',
                  padding: '50px',
                  transitionDelay: `${index * 0.1}s`
                }}
              >
                <IconComponent 
                  size={48} 
                  color="var(--brand-primary)" 
                  className="icon-badge"
                  style={{ marginBottom: '24px' }}
                />
                
                <h3 className="heading-1" style={{ marginBottom: '16px' }}>
                  {point.title}
                </h3>
                
                <p className="body-medium" style={{ 
                  marginBottom: '32px',
                  lineHeight: 1.6,
                  color: 'var(--text-secondary)'
                }}>
                  {point.description}
                </p>
                
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {point.details.map((detail, detailIndex) => (
                    <li 
                      key={detailIndex} 
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        marginBottom: '12px',
                        fontSize: '16px',
                        color: 'var(--text-secondary)',
                        lineHeight: 1.5
                      }}
                    >
                      <div style={{
                        width: '6px',
                        height: '6px',
                        backgroundColor: 'var(--brand-primary)',
                        borderRadius: '50%',
                        marginTop: '8px',
                        flexShrink: 0
                      }} />
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        
        {/* Security Guarantee */}
        <div className="reveal-scale" style={{
          textAlign: 'center',
          marginTop: '80px',
          padding: '40px',
          background: 'var(--bg-primary)',
          border: '2px solid var(--brand-primary)',
          borderRadius: '6px'
        }}>
          <h3 className="heading-1" style={{ 
            marginBottom: '16px',
            color: 'var(--brand-primary)'
          }}>
            Security-First Promise
          </h3>
          <p className="body-large" style={{ 
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: 1.6
          }}>
            Every QuByt AI application launches with enterprise-grade security by default. 
            No additional configuration, no security debt, no compromise.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Security;